// BPA HTML Vault session lifecycle manager.
// Keeps the existing 30-minute security window, but treats it as an
// inactivity timeout instead of an absolute timeout from login.

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 15 * 1000;

const PASSCODE_KEY = 'BPA_SESSION_PASSCODE';
const EXPIRY_KEY = 'BPA_SESSION_EXPIRY';
const LAST_ACTIVITY_KEY = 'BPA_SESSION_LAST_ACTIVITY';
const EXPIRED_NOTICE_KEY = 'BPA_SESSION_EXPIRED_NOTICE';
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'pointermove'];

let lastActivityWriteAt = 0;
let expiryOverlay = null;
let expiryHandled = false;
const trackedFrames = new WeakSet();

const readNumber = (key) => {
  const value = sessionStorage.getItem(key);
  if (!value) return 0;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasSessionCredential = () => Boolean(sessionStorage.getItem(PASSCODE_KEY));

const removeExpiryOverlay = () => {
  if (expiryOverlay && expiryOverlay.parentNode) {
    expiryOverlay.parentNode.removeChild(expiryOverlay);
  }
  expiryOverlay = null;
};

const showExpiryOverlay = () => {
  if (expiryOverlay || typeof document === 'undefined') return;

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'bpa-session-expired-title');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:24px',
    'background:rgba(15,23,42,0.72)',
    'backdrop-filter:blur(5px)'
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = [
    'width:min(440px,100%)',
    'background:#ffffff',
    'border-radius:18px',
    'box-shadow:0 24px 70px rgba(15,23,42,0.3)',
    'padding:30px',
    'font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    'color:#1e293b',
    'text-align:center'
  ].join(';');

  const icon = document.createElement('div');
  icon.textContent = '🔒';
  icon.setAttribute('aria-hidden', 'true');
  icon.style.cssText = 'font-size:34px;margin-bottom:12px';

  const title = document.createElement('h2');
  title.id = 'bpa-session-expired-title';
  title.textContent = 'Session expired';
  title.style.cssText = 'margin:0 0 10px;font-size:22px;font-weight:800;color:#0f172a';

  const message = document.createElement('p');
  message.textContent = 'Your Vault session was locked because of inactivity. Please enter your passcode again to continue.';
  message.style.cssText = 'margin:0 0 22px;font-size:14px;line-height:1.6;color:#64748b';

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Unlock Vault';
  button.style.cssText = [
    'width:100%',
    'border:0',
    'border-radius:12px',
    'padding:12px 18px',
    'background:#0f172a',
    'color:#ffffff',
    'font-size:14px',
    'font-weight:800',
    'cursor:pointer'
  ].join(';');

  button.addEventListener('click', () => {
    sessionStorage.removeItem(EXPIRED_NOTICE_KEY);
    removeExpiryOverlay();
    window.location.reload();
  });

  card.append(icon, title, message, button);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  expiryOverlay = overlay;
  window.setTimeout(() => button.focus(), 0);
};

const expireForInactivity = () => {
  if (expiryHandled || !hasSessionCredential()) return;
  expiryHandled = true;

  // Clear only authentication/session lifecycle keys. Do not touch IndexedDB,
  // local template data, categories, Google sync configuration, or backups.
  sessionStorage.removeItem(PASSCODE_KEY);
  sessionStorage.removeItem(EXPIRY_KEY);
  sessionStorage.removeItem(LAST_ACTIVITY_KEY);
  sessionStorage.setItem(EXPIRED_NOTICE_KEY, '1');

  showExpiryOverlay();
};

const checkForInactivityExpiry = () => {
  if (!hasSessionCredential()) return false;

  const now = Date.now();
  const expiry = readNumber(EXPIRY_KEY);
  const lastActivity = readNumber(LAST_ACTIVITY_KEY);

  if ((expiry && now >= expiry) || (lastActivity && now - lastActivity >= SESSION_TIMEOUT_MS)) {
    expireForInactivity();
    return true;
  }

  return false;
};

const recordActivity = ({ force = false } = {}) => {
  const now = Date.now();

  // Record pre-login activity too, so the successful login starts with the
  // user's actual interaction time rather than an unrelated timer.
  if (!force && now - lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) return;
  lastActivityWriteAt = now;

  if (checkForInactivityExpiry()) return;

  sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now));

  // db.js still creates the original 30-minute expiry after passcode
  // validation. While a credential is present, extend that same key only on
  // real user activity. Existing sync/auth code therefore remains compatible.
  if (hasSessionCredential()) {
    sessionStorage.setItem(EXPIRY_KEY, String(now + SESSION_TIMEOUT_MS));
  }
};

const handleReturnToApp = () => {
  // Check first. Returning after 30+ minutes must expire the session rather
  // than count the focus/visibility event itself as fresh activity.
  if (!checkForInactivityExpiry()) {
    recordActivity({ force: true });
  }
};

const bindActivityTarget = (target) => {
  if (!target || typeof target.addEventListener !== 'function') return;
  ACTIVITY_EVENTS.forEach((eventName) => {
    target.addEventListener(eventName, () => recordActivity(), { passive: true });
  });
};

const bindTemplateFrame = (frame) => {
  if (!frame || trackedFrames.has(frame)) return;
  trackedFrames.add(frame);

  const attach = () => {
    try {
      // Vault templates use srcDoc with allow-same-origin, so their document is
      // normally accessible. If a template later navigates cross-origin, the
      // access attempt is safely ignored rather than affecting the template.
      bindActivityTarget(frame.contentWindow);
      bindActivityTarget(frame.contentDocument);
    } catch (error) {
      console.debug('[Session] Template activity bridge unavailable:', error?.message || error);
    }
  };

  frame.addEventListener('load', attach);
  attach();
};

const bindCurrentAndFutureTemplateFrames = () => {
  document.querySelectorAll('iframe').forEach(bindTemplateFrame);

  if (!document.body) return;
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.tagName === 'IFRAME') bindTemplateFrame(node);
        node.querySelectorAll?.('iframe').forEach(bindTemplateFrame);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

const initialiseSessionLifecycle = () => {
  if (sessionStorage.getItem(EXPIRED_NOTICE_KEY) === '1') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', showExpiryOverlay, { once: true });
    } else {
      showExpiryOverlay();
    }
  }

  // If this is an already-valid session upgraded from the prior version,
  // initialise activity tracking without invalidating it early.
  if (hasSessionCredential() && !readNumber(LAST_ACTIVITY_KEY)) {
    const now = Date.now();
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    sessionStorage.setItem(EXPIRY_KEY, String(now + SESSION_TIMEOUT_MS));
  }

  bindActivityTarget(window);

  window.addEventListener('focus', handleReturnToApp);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) handleReturnToApp();
  });

  const bindFramesWhenReady = () => bindCurrentAndFutureTemplateFrames();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindFramesWhenReady, { once: true });
  } else {
    bindFramesWhenReady();
  }

  window.setInterval(checkForInactivityExpiry, CHECK_INTERVAL_MS);
};

initialiseSessionLifecycle();